/**
 * Viewport synchronizers: WL, scroll (image slice), camera (zoom/pan).
 *
 * Each synchronizer is created once and reused; we (re)register viewports
 * whenever the set of active viewports changes. Sync flags in viewerStore
 * enable/disable propagation — we implement this by adding viewports to the
 * synchronizer when enabled and removing them when disabled.
 */
import {
  synchronizers,
  SynchronizerManager,
} from '@cornerstonejs/tools';
import { RENDERING_ENGINE_ID } from './init';

const VOI_SYNC_ID = 'edgeweb-voi-sync';
const SLICE_SYNC_ID = 'edgeweb-slice-sync';
const CAMERA_SYNC_ID = 'edgeweb-camera-sync';

function getOrCreateVOISync() {
  const existing = SynchronizerManager.getSynchronizer(VOI_SYNC_ID);
  if (existing) return existing;
  return synchronizers.createVOISynchronizer(VOI_SYNC_ID, {
    syncInvertState: true,
    syncColormap: false,
  });
}

function getOrCreateSliceSync() {
  const existing = SynchronizerManager.getSynchronizer(SLICE_SYNC_ID);
  if (existing) return existing;
  return synchronizers.createImageSliceSynchronizer(SLICE_SYNC_ID);
}

function getOrCreateCameraSync() {
  const existing = SynchronizerManager.getSynchronizer(CAMERA_SYNC_ID);
  if (existing) return existing;
  return synchronizers.createCameraPositionSynchronizer(CAMERA_SYNC_ID);
}

function setSyncMembership(
  sync: any,
  viewportIds: string[],
  active: boolean
) {
  if (!sync) return;
  // Remove any existing members first so toggling off fully detaches
  try {
    const sources = sync.getSourceViewports?.() ?? [];
    const targets = sync.getTargetViewports?.() ?? [];
    [...sources, ...targets].forEach((v: any) => {
      try { sync.remove({ renderingEngineId: v.renderingEngineId, viewportId: v.viewportId }); } catch {}
    });
  } catch {}

  if (!active) return;
  viewportIds.forEach((vpId) => {
    try {
      sync.add({ renderingEngineId: RENDERING_ENGINE_ID, viewportId: vpId });
    } catch {}
  });
}

export function applySyncState(opts: {
  viewportIds: string[];
  /** Subset eligible for camera sync. Camera sync copies absolute pose, which
   *  only makes sense between viewports that share a coordinate frame (same
   *  volume, or the same image stack). For mixed stacks / unrelated series
   *  this would otherwise send zoom/pan state that lands off-image and leaves
   *  viewports blank. Pass a filtered list here; defaults to all. */
  cameraViewportIds?: string[];
  syncWL: boolean;
  syncScroll: boolean;
  syncCamera: boolean;
}) {
  const { viewportIds, cameraViewportIds, syncWL, syncScroll, syncCamera } = opts;
  setSyncMembership(getOrCreateVOISync(), viewportIds, syncWL);
  setSyncMembership(getOrCreateSliceSync(), viewportIds, syncScroll);
  setSyncMembership(
    getOrCreateCameraSync(),
    cameraViewportIds ?? viewportIds,
    syncCamera && (cameraViewportIds ?? viewportIds).length >= 2
  );
}

export function destroySynchronizers() {
  [VOI_SYNC_ID, SLICE_SYNC_ID, CAMERA_SYNC_ID].forEach((id) => {
    try { SynchronizerManager.destroySynchronizer(id); } catch {}
  });
}
